import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import PermissionButton from 'components/buttons/PermissionButton';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import GridItemList, { Row, spansRow } from 'components/GridItemList';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    ActiveChip,
    META_LINK_CLASS,
    MetaItem,
    RECORD_GRID_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordPage,
} from 'components/record-header/RecordHeader';
import TabsGallery, { TabType } from 'components/TabsGallery';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useGetPracticeAreaByIdQuery } from 'store/api/practice-areas';
import { ClassifierData } from 'types/classifiers';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { PracticeArea, PracticeAreaType } from 'types/practice-areas';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import CustomFieldCards from '../offices/CustomFieldCards';
import { PracticeAreaSection } from './form-sections/PracticeAreaSection';

const USED_CLASSIFIERS = ['PRACTICE_AREA_TYPE'];
const UPDATE_PRACTICE_AREAS = ['UPDATE.PRACTICE_AREAS'];
const yesNo = (value: boolean | undefined) => (value ? t('yes') : t('no'));

/** The practice area's read rows by name: the classic Overview lists them in one card, the new design by topic. */
const practiceAreaRows = (practiceArea: PracticeArea, classifiers: Record<string, ClassifierData>) =>
    ({
        name: { label: t('practice-areas.name'), content: practiceArea.name || '-' },
        type: {
            label: t('practice-areas.practice-area-type'),
            content: getClassifierText(classifiers['PRACTICE_AREA_TYPE'], practiceArea.practiceAreaTypeCode) || '-',
        },
        parent: {
            label: t('practice-areas.parent-practice-area'),
            content: practiceArea.parentPracticeArea?.id ? (
                <RecordLink
                    className="entity-link hover:underline"
                    to={`/practice-area/${practiceArea.parentPracticeArea.id}`}
                    label={formatPracticeAreaLabel(practiceArea.parentPracticeArea)}
                />
            ) : (
                '-'
            ),
        },
        practiceGroup: {
            label: t('practice-areas.practice-group'),
            content: practiceArea.practiceGroup?.id ? (
                <RecordLink
                    className="entity-link hover:underline"
                    to={`/team/${practiceArea.practiceGroup.id}`}
                    label={practiceArea.practiceGroup.name}
                />
            ) : (
                '-'
            ),
        },
        practiceTeams: {
            label: t('practice-areas.practice-teams'),
            content: practiceArea.practiceTeams?.length ? (
                <div className="flex flex-col">
                    {practiceArea.practiceTeams.map((team) => (
                        <RouterLink className="entity-link w-fit hover:underline" key={team.id} to={`/team/${team.id}`}>
                            {team.name}
                        </RouterLink>
                    ))}
                </div>
            ) : (
                '-'
            ),
        },
        aml: { label: t('practice-areas.aml-applicable'), content: yesNo(practiceArea.isAmlApplicable) },
        excludedFromAutomaticClosing: {
            label: t('case-closing-practice-area.excluded-from-automatic-closing'),
            content: yesNo(practiceArea.isExcludedFromAutomaticClosing),
        },
        active: { label: t('active'), content: yesNo(practiceArea.isActive) },
        showInTimecards: { label: t('practice-areas.show-in-timecards'), content: yesNo(practiceArea.showInTimecards) },
        showInCases: { label: t('practice-areas.show-in-cases'), content: yesNo(practiceArea.showInCases) },
        showInEmployees: { label: t('practice-areas.show-in-employees'), content: yesNo(practiceArea.showInEmployees) },
    }) satisfies Record<string, Row>;

const PracticeAreaPage = (): JSX.Element => {
    const { id: practiceAreaId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/practice-areas');
    const isNew = useIsNewDesign();
    const canEdit = useHasPermissions(UPDATE_PRACTICE_AREAS);
    const isSubPracticeAreaEnabled = useIsFeatureEnabled(FeatureFlag.SUB_PRACTICE_AREA);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: practiceArea, isLoading: isLoadingPracticeArea } = useGetPracticeAreaByIdQuery(
        practiceAreaId ?? skipToken,
        { refetchOnMountOrArgChange: true }
    );
    const isDraft = !!practiceArea?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/practice-areas');
    const draftActions = useDraftActions({
        draft: practiceArea?.isDraft ? { type: 'practice-area', id: practiceArea.id } : null,
        createRoute: '/practice-area/new',
        onDeleted: leaveDeleted,
    });
    const missingFields = draftActions.draft?.missingFields;
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({ codes: USED_CLASSIFIERS });

    const { customFields, customFieldsFiltered } = useCustomFields({
        entityType: EntityType.PRACTICE_AREA,
        fields: practiceArea?.customFields,
    });

    const rows = useMemo(
        () => (practiceArea ? practiceAreaRows(practiceArea, classifiers) : undefined),
        [practiceArea, classifiers]
    );
    const generalRows: Row[] = useMemo(() => {
        if (!rows) return [];
        const list = [
            rows.name,
            rows.type,
            rows.parent,
            rows.practiceGroup,
            rows.practiceTeams,
            rows.aml,
            rows.excludedFromAutomaticClosing,
            rows.active,
            rows.showInTimecards,
            rows.showInCases,
            rows.showInEmployees,
            ...customFieldsFiltered.map((field) => ({
                label: field.label ? (field.shouldTranslate ? t(field.label) : field.label) : '',
                content: <CustomFieldDisplay key={field.id} field={field} classifiers={allClassifiers} />,
            })),
        ];
        return withMissing(list, missingFields);
    }, [rows, allClassifiers, customFieldsFiltered, missingFields]);

    const tabs = useMemo<TabType[]>(() => {
        if (!practiceArea || !rows) return [];
        const auditInfo = (
            <AuditInfo
                createdAt={practiceArea.createdAt}
                createdByEmployee={practiceArea.createdByEmployee}
                lastModifiedAt={practiceArea.lastModifiedAt ?? undefined}
                changedByEmployee={practiceArea.changedByEmployee}
            />
        );
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: isNew ? (
                    <div className={RECORD_GRID_CLASS}>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            <PracticeAreaSection id="practice-area" titleId="practice-area" practiceArea={practiceArea}>
                                {(edit) => (
                                    // The form's fields in the form's order, each where the form shows it.
                                    <GridItemList
                                        isNew
                                        titleId="practice-area"
                                        data={withMissing(
                                            [
                                                // Its radio options take a row of their own, whatever is picked.
                                                { ...rows.type, wide: 'always' },
                                                rows.name,
                                                {
                                                    ...rows.parent,
                                                    hidden:
                                                        practiceArea.practiceAreaTypeCode !==
                                                            PracticeAreaType.PRACTICE_SUBAREA ||
                                                        !isSubPracticeAreaEnabled,
                                                },
                                                rows.practiceGroup,
                                                {
                                                    // The teams follow the group, so the form only shows them: the
                                                    // same text, on a row of its own while there are any, as the
                                                    // form draws it.
                                                    ...rows.practiceTeams,
                                                    wide: spansRow(practiceArea.practiceTeams),
                                                    content: (
                                                        <ReadonlyField
                                                            value={practiceArea.practiceTeams ?? []}
                                                            getLabel={(team) => team?.name}
                                                            getValue={(team) => team?.id}
                                                            linkTo="team"
                                                        />
                                                    ),
                                                },
                                                rows.aml,
                                                rows.excludedFromAutomaticClosing,
                                                rows.active,
                                            ],
                                            missingFields
                                        )}
                                        button={edit}
                                    />
                                )}
                            </PracticeAreaSection>
                            {customFieldsFiltered.length > 0 ? (
                                <PracticeAreaSection
                                    id="custom-fields"
                                    titleId="custom-fields.title"
                                    practiceArea={practiceArea}
                                >
                                    {(edit) => (
                                        <CustomFieldCards
                                            fields={customFields}
                                            classifiers={allClassifiers}
                                            edit={edit}
                                        />
                                    )}
                                </PracticeAreaSection>
                            ) : null}
                            {auditInfo}
                        </div>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {isDraft ? <DraftSharedWith /> : null}
                            <PracticeAreaSection
                                id="visibility"
                                titleId="practice-areas.visibility"
                                practiceArea={practiceArea}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="practice-areas.visibility"
                                        data={[rows.showInTimecards, rows.showInCases, rows.showInEmployees]}
                                        button={edit}
                                    />
                                )}
                            </PracticeAreaSection>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <GridItemList titleId="matters.overview" data={generalRows} key="practice-area-general" />
                        {isDraft ? <DraftSharedWith /> : null}
                        {auditInfo}
                    </div>
                ),
            },
        ];
    }, [
        practiceArea,
        rows,
        generalRows,
        isNew,
        customFields,
        customFieldsFiltered,
        allClassifiers,
        isSubPracticeAreaEnabled,
        isDraft,
        missingFields,
    ]);

    if (isLoadingPracticeArea || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!practiceArea) {
        return <></>;
    }

    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="practice-area-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const draft = draftActions.draft;
    const title = isDraft && !practiceArea.name ? t('new-entity.practice-area') : formatPracticeAreaLabel(practiceArea);
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    if (isNew) {
        const typeLabel = getClassifierText(classifiers['PRACTICE_AREA_TYPE'], practiceArea.practiceAreaTypeCode);
        const header = (
            <RecordHeader
                chips={
                    <>
                        {isDraft ? <DraftChip /> : null}
                        {typeLabel ? <StatusChip type="neutral" label={typeLabel} /> : null}
                        <ActiveChip isActive={!!practiceArea.isActive} />
                        {practiceArea.isAmlApplicable ? (
                            <StatusChip type="warning" label={t('practice-areas.aml-applicable')} />
                        ) : null}
                    </>
                }
                title={title}
                pin={isDraft ? undefined : { kind: 'practice-area', id: practiceArea.id }}
                meta={
                    <>
                        {practiceArea.parentPracticeArea?.id ? (
                            <MetaItem label={<FormattedMessage id="practice-areas.parent-practice-area" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/practice-area/${practiceArea.parentPracticeArea.id}`}
                                    label={formatPracticeAreaLabel(practiceArea.parentPracticeArea)}
                                />
                            </MetaItem>
                        ) : null}
                        {practiceArea.practiceGroup?.id ? (
                            <MetaItem label={<FormattedMessage id="practice-areas.practice-group" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/team/${practiceArea.practiceGroup.id}`}
                                    label={practiceArea.practiceGroup.name}
                                />
                            </MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={isDraft ? <DraftRecordActions actions={draftActions} /> : undefined}
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={canEdit && !isDraft}>{tabsGallery}</SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        <div className="flex items-center justify-end gap-2">
                            {isDraft ? (
                                <DraftRecordActions actions={draftActions} />
                            ) : (
                                <PermissionButton
                                    permissions={UPDATE_PRACTICE_AREAS}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/practice-area/${practiceAreaId}/edit`)}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            )}
                        </div>
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default PracticeAreaPage;
